<template>
  <div>
    <h3 v-if="showLabel" class="mb-2 flex items-center text-xs">
      <component :is="icon" class="w-4 h-4 inline mr-1" />
      {{ label }}
    </h3>
    
    <!-- Read-only display when not editable -->
    <div v-if="!editable" class="w-full text-xs flex items-center h-9 px-3 cursor-default ">
      <span
        :class="optionClasses"
        class="inline-block px-3 py-1 rounded-full"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div v-else-if="disabled" class="w-full text-xs flex items-center h-9 px-3 cursor-default border-[1px] border-gray-200 rounded-md">
      <span
        :class="optionClasses"
        class="inline-block px-3 py-1 rounded-full"
      >
        {{ statusLabel }}
      </span>
    </div>

    <!-- Editable dropdown -->
    <Select
      v-else
      :model-value="modelValue"
      :disabled="disabled || loading"
      @update:model-value="(value) => value && $emit('update:modelValue', value as string)"
      >
      <SelectTrigger class="w-full">
        <SelectValue>
          <div class="flex items-center gap-2">
            <span
              :class="optionClasses"
              class="px-2 py-0.5 rounded-full text-xs"
            >
              {{ statusLabel }}
            </span>
            <Loader2 v-if="isMatchPending" class="w-3 h-3 animate-spin text-gray-500" />
            <Loader2 v-if="loading && !isMatchPending" class="w-3 h-3 animate-spin text-gray-400" />
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem
          v-for="option in options"
          :key="option.value"
          :value="option.value"
        >
          <span :class="[option.bgColor, option.textColor]" class="px-2 py-0.5 rounded-full text-xs">
            {{ option.label }}
          </span>
        </SelectItem>
      </SelectContent>
    </Select>

    <p v-if="selectedApplication?.referrer_details" class="text-xs text-gray-500 mt-2">
      <span class="font-semibold">Assigned referrer:</span> {{ selectedApplication?.referrer_details?.name }}
    </p>
    <p v-if="statusType === 'referralStatus' && selectedApplication?.referral_tier_allowed === false && !minimized" class="text-xs text-gray-500 mt-2">
      <span class="font-semibold">{{ REFERRAL_TIER_UNAVAILABLE_MESSAGE }}</span>
    </p>
    <p v-else-if="statusType === 'referralStatus' && !selectedApplication?.referrers_exist_for_employer && !minimized" class="text-xs text-gray-500 mt-2">
      <span class="font-semibold">There are currently no active Outtalent referrers for this employer</span>
    </p>
    <p v-else-if="showComment" class="text-xs text-gray-500 mt-2">{{ statusComment }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralTypeOptions,
  referralStatusOptions,
  REFERRAL_TIER_UNAVAILABLE_MESSAGE
} from '@/const/applicationStatuses'

type StatusType = 'application' | 'interview' | 'referralType' | 'referralStatus'

const props = defineProps<{
  modelValue: string
  statusType: StatusType
  label?: string
  icon?: any
  showLabel?: boolean
  showComment?: boolean
  editable?: boolean
  disabled?: boolean
  filteredOptions?: any[]
  selectedApplication?: any
  minimized?: boolean
  loading?: boolean
}>()

defineEmits<{
  'update:modelValue': [value: string]
}>()

const statusOptionsMap = {
  application: applicationStatusOptions,
  interview: interviewStatusOptions,
  referralType: referralTypeOptions,
  referralStatus: referralStatusOptions
}

const options = computed(() => {
  if (props.filteredOptions) {
    return props.filteredOptions
  }
  return statusOptionsMap[props.statusType]
})

const currentOption = computed(() => {
  // First try to find in filtered options
  const filteredOption = options.value.find(option => option.value === props.modelValue)
  if (filteredOption) {
    return filteredOption
  }
  
  // If not found in filtered options, look in the full status options
  // This handles cases where current status is internal but needs styling
  const fullOptions = statusOptionsMap[props.statusType]
  return fullOptions.find(option => option.value === props.modelValue)
})

const statusLabel = computed(() => 
  currentOption.value?.label || props.modelValue
)

const optionClasses = computed(() => 
  currentOption.value ? [currentOption.value.bgColor, currentOption.value.textColor] : []
)

const statusComment = computed(() => 
  currentOption.value?.text || ''
)

const isMatchPending = computed(() => 
  props.statusType === 'referralStatus' && 
  props.modelValue === 'READY_FOR_REFERRAL' && 
  props.selectedApplication?.match_status === 'PENDING'
)
</script>